// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { RootState } from "../../../app/store"
import { grpc } from "../../../app/rpc/executor"
import { narratePreferencesClient } from "../../../app/rpc/client"
import { showSnackBar } from "../../snackbar/actions"
import {
  GetTrainingNarrativeRequest,
  ListTrainingGroupsRequest,
  ListTrainingGroupsResponse,
} from "../../../generated/wisetime/user/assistant/v4/preferences_pb"

type ListTrainingGroupsRpc = (request: ListTrainingGroupsRequest) => Observable<ListTrainingGroupsResponse>

export const listTrainingGroups = (listTrainingGroupsRpc: ListTrainingGroupsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listTrainingGroups.match),
      mergeMap(action => of(action.payload.request).pipe(
        mergeMap(listTrainingGroupsRpc),
        withLatestFrom(state$),
        mergeMap(([response, state]) => {
          const { trainingGroupsList } = response.toObject()
          if (action.payload.replaceGroupId
            && trainingGroupsList.length === 0
            && action.payload.request.getExcludeTrainingGroupIdsList().length !== 0
          ) {
            return concat([
              Slice.resetReplacedGroupIds(),
              Actions.listTrainingGroups({
                request: new ListTrainingGroupsRequest().setCount(action.payload.request.getCount()),
                replaceGroupId: action.payload.replaceGroupId,
              }),
            ])
          }
          const actions: Action[] = []
          if (action.payload.replaceGroupId && trainingGroupsList[0]) {
            actions.push(Slice.setTrainingGroup({
              trainingGroup: trainingGroupsList[0],
              replaceGroupId: action.payload.replaceGroupId,
            }))
          } else {
            actions.push(Slice.setTrainingGroups(trainingGroupsList))
          }
          response.getTrainingGroupsList().forEach(group => {
            if (state.narratePreferences.previewNarratePreferences) {
              actions.push(Actions.getTrainingNarrative(
                new GetTrainingNarrativeRequest()
                  .setTrainingGroup(group)
                  .setPreferences(Slice.toNarratePreferencesMessage(
                    state.narratePreferences.previewNarratePreferences,
                  ))
              ))
            } else if (state.narratePreferences.savedNarratePreferences) {
              actions.push(Actions.getTrainingNarrative(
                new GetTrainingNarrativeRequest()
                  .setTrainingGroup(group)
                  .setPreferences(Slice.toNarratePreferencesMessage(
                    state.narratePreferences.savedNarratePreferences,
                  ))
              ))
            }
          })
          return concat(actions)
        }),
        catchError(_ => of(showSnackBar({
          message: t({
            id: "msg_list_training_groups_failed",
            message: "Failed to load groups. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
          }),
          messageType: "ERROR",
        }))),
      )),
    )

const listTrainingGroupsRpc = (request: ListTrainingGroupsRequest): Observable<ListTrainingGroupsResponse> =>
  grpc(() => narratePreferencesClient.listTrainingGroups(request, null))

export default listTrainingGroups(listTrainingGroupsRpc)
